import React, { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FieldValues,
  UseFieldArrayAppend,
  useFormContext,
  UseFormSetValue,
} from 'react-hook-form';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import _replace from 'lodash/replace';
import classNames from 'classnames';
import {
  FormChecklistTemplateOption,
  FormTemplateQuestionFieldOptionConditionEnum,
  FormTemplateQuestionFieldOptionGroupEnum,
  FormTemplateQuestionFieldOptionsValidityHintEnum,
  FormTemplateQuestionFieldsKindEnum,
} from 'graphql-common';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import Popover from '@lib/components/Popover/Popover';
import Menu from '@lib/components/Menu/Menu';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import getConditionNavItems from 'components/FormTemplateBuilder/utils/getConditionNavItems';
import getValidityHintOptionByValue from 'components/FormTemplateBuilder/utils/getValidityHintOptionByValue';
import { AppendValue, FormRef } from '@lib/components/ReactHookForm/types';
import { materialSymbolsOutlined } from '@lib/enums/common';
import { readFileData } from 'routes/FormChecklists/utils/readFileData';
import { captureException } from '@lib/utils/captureException';
import {
  FileSizeLimits,
  formOptionsFileSizeLimit,
  UppyDataImportAllowedFileTypes,
} from '@lib/enums/mimeTypes';
import FileUploader from '@lib/components/FilesUploader/FileUploader';
import FormChecklistSelectModalForm from 'components/FormTemplateBuilder/FormChecklistSelectModalForm/FormChecklistSelectModalForm';
import { SupportiveMessageColor } from '@lib/components/SupportiveMessage/SupportiveMessage';
import downloadHandler from '@lib/utils/downloadHandler';
import { getNewOptionValue } from '@lib/utils/formOptionsHelpers';
import toast from '@lib/components/Toast/Toast';
import styles from './QuestionFieldOptionsAddItemComponent.module.scss';
import {
  kindFieldsWithAddOption,
  kindFieldsWithCondition,
  kindFieldsWithPreviewForEditMode,
  kindFieldsWithValidityHint,
} from '../constants';

const { VITE_CDN_URL } = import.meta.env;

interface Props {
  append: UseFieldArrayAppend<FieldValues, string>;
  appendCallback: () => void;
  appendValue: AppendValue;
  isAddMoreVisible: boolean;
  parentName: string;
}

const renderButton = (
  onClick: () => void,
  appendValue: object | undefined,
  t: TFunction<'translation', undefined>,
  extraProps: Record<string, any> = {},
) => {
  const buttonText = (() => {
    const group = _get(appendValue, 'group');
    if (group === FormTemplateQuestionFieldOptionGroupEnum.Rows)
      return t('add-row');
    if (group === FormTemplateQuestionFieldOptionGroupEnum.Columns)
      return t('add-column');
    return t('add-option');
  })();

  return (
    <Button
      buttonText={buttonText}
      buttonType={ButtonTypes.secondaryOutlined}
      buttonSize={ButtonSizes.small}
      leftIcon="add"
      leftIconClassName={materialSymbolsOutlined}
      onClick={onClick}
      {...extraProps}
    />
  );
};

function QuestionFieldOptionsAddItemComponentForFieldsWithCondition(
  props: Pick<Props, 'append' | 'appendValue'> & {
    handleCallback: () => void;
    className: string;
  },
) {
  const { append, appendValue = {}, handleCallback, className } = props;
  const { t } = useTranslation();

  const handleAddCondition = (
    condition: FormTemplateQuestionFieldOptionConditionEnum,
  ) => {
    const newAppendValue = {
      ...appendValue,
      values:
        condition === FormTemplateQuestionFieldOptionConditionEnum.Between
          ? ['', '']
          : [''],
    };
    append({
      ...newAppendValue,
      validityHint: getValidityHintOptionByValue(
        FormTemplateQuestionFieldOptionsValidityHintEnum.Success,
      ),
      condition,
    });
    handleCallback();
  };
  const popoverBody = () => (
    <div className={styles.popoverBody}>
      <Menu
        navItems={getConditionNavItems(handleAddCondition, t)}
        itemClassName={styles.menuItemClassName}
      />
    </div>
  );
  return (
    <div className={className}>
      <Popover body={popoverBody}>
        {({ handleToggle, isOpen }) => (
          <div>
            {renderButton(handleToggle, appendValue, t, {
              isFocused: isOpen,
            })}
          </div>
        )}
      </Popover>
    </div>
  );
}

function QuestionFieldOptionsAddItemComponentForChecklist(
  props: Pick<Props, 'append' | 'appendValue' | 'parentName'> & {
    className: string;
    clearErrors: () => void;
    handleCallback: () => void;
    setValue: UseFormSetValue<FieldValues>;
  },
) {
  const {
    append,
    appendValue = {},
    className,
    clearErrors,
    handleCallback,
    parentName,
    setValue,
  } = props;
  const { i18n, t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const formRef = useRef<FormRef>(undefined);

  const uploadButtonProps = {
    buttonText: t('upload-file'),
    buttonType: ButtonTypes.primaryOutlined,
    buttonSize: ButtonSizes.small,
    leftIcon: 'upload',
    fullWidth: true,
  };

  const onDownloadTemplate = () => {
    downloadHandler(
      `${VITE_CDN_URL}/templates/${i18n.language}/import/Checklists-import-template.xlsx`,
    );
  };

  const supportiveMessageProps = {
    color: SupportiveMessageColor.Blue,
    text: t('import-file-attention'),
    actions: [
      <Button
        key="download-template"
        leftIcon="download"
        leftIconClassName={materialSymbolsOutlined}
        buttonText={t('download-template')}
        buttonType={ButtonTypes.primaryText}
        buttonSize={ButtonSizes.small}
        onClick={onDownloadTemplate}
      />,
    ],
  };

  const handleAddChecklist = () => {
    append({
      ...getNewOptionValue(),
      ...appendValue,
    });
    handleCallback();
  };

  const handleAddChecklistFromFile = (v) => {
    readFileData(v)
      .then((res) => {
        const newOptions = res
          .filter((option) => {
            let optionValue = option[0];
            if (typeof optionValue === 'string') {
              optionValue = optionValue.trim();
              return !!optionValue && optionValue?.[0] !== '⋄';
            }
            return false;
          })
          .map((option, position) => ({
            values: [option[0] ? `${option[0]}` : ''],
            position,
          }));
        clearErrors();
        if (newOptions.length) {
          setValue(parentName, newOptions);
        } else {
          toast({ content: t('no-options-import-error') });
        }
      })
      .catch((error) => {
        captureException(error);
      });
  };

  const onSubmit = (values?: FormChecklistTemplateOption[]) => {
    const newOptions = (values || [])
      .map((option, position) => ({
        values: [option.value ? `${option.value}` : ''],
        position,
      }))
      .sort((a, b) => a.position - b.position);
    if (newOptions.length) {
      clearErrors();
      setValue(parentName, newOptions, {
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  };

  const onOpen = () => setIsModalOpen(true);

  const onClose = () => setIsModalOpen(false);

  return (
    <>
      <div className={className}>
        {renderButton(handleAddChecklist, appendValue, t)}
        <div className={styles.actionsRow}>
          {renderButton(onOpen, undefined, t, {
            buttonText: t('use-template'),
            buttonType: ButtonTypes.primaryOutlined,
            leftIcon: 'draft',
          })}
          <FileUploader
            allowedFileTypes={UppyDataImportAllowedFileTypes}
            maxFileSize={formOptionsFileSizeLimit}
            note={t('data-import-upload-validation-msg', {
              size: FileSizeLimits.FormOptionsFile,
            })}
            onChange={handleAddChecklistFromFile}
            showOfflineMessage
            supportiveMessageProps={supportiveMessageProps}
            uploadButtonProps={uploadButtonProps}
            useUploadStrategy={false}
          />
        </div>
      </div>
      <FormChecklistSelectModalForm
        formRef={formRef}
        onSubmit={onSubmit}
        onClose={onClose}
        isOpen={isModalOpen}
        onCancel={onClose}
      />
    </>
  );
}

function QuestionFieldOptionsAddItemComponent(props: Props) {
  const {
    append,
    appendCallback,
    appendValue = {},
    isAddMoreVisible,
    parentName,
  } = props;
  const { t } = useTranslation();
  const { getValues, setValue, clearErrors } = useFormContext();
  const allFormValues = getValues();
  const fieldValue = _get(
    allFormValues,
    _replace(parentName, `.${FormTemplateFieldNames.Options}`, ''),
  );
  const kind = _get(fieldValue, FormTemplateFieldNames.Kind);
  const options = _get(fieldValue, FormTemplateFieldNames.Options, []);

  const className = classNames(styles.optionAddBtn, {
    [styles.optionAddBtnWithTopPadding]:
      options?.length &&
      kind !== FormTemplateQuestionFieldsKindEnum.CheckboxButtonGrid,
    [styles.optionAddBtnWithBottomPadding]:
      kindFieldsWithPreviewForEditMode.includes(kind),
    [styles.optionAddBtnRowWithActions]:
      kind === FormTemplateQuestionFieldsKindEnum.Checklist,
  });

  const handleCallback = () => {
    if (appendCallback) appendCallback();
  };

  const handleAddWithHint = () => {
    append({
      ...appendValue,
      validityHint: getValidityHintOptionByValue(
        FormTemplateQuestionFieldOptionsValidityHintEnum.Success,
      ),
    });
    handleCallback();
  };

  const handleAddDefault = () => {
    append(appendValue);
    handleCallback();
  };

  if (!isAddMoreVisible || !append || !kindFieldsWithAddOption.includes(kind)) {
    return null;
  }

  if (kindFieldsWithCondition.includes(kind)) {
    return (
      <QuestionFieldOptionsAddItemComponentForFieldsWithCondition
        {...props}
        handleCallback={handleCallback}
        className={className}
      />
    );
  }

  if (kindFieldsWithValidityHint.includes(kind)) {
    return (
      <div className={className}>
        {renderButton(handleAddWithHint, appendValue, t)}
      </div>
    );
  }

  if (kind === FormTemplateQuestionFieldsKindEnum.Checklist) {
    return (
      <QuestionFieldOptionsAddItemComponentForChecklist
        {...props}
        className={className}
        clearErrors={clearErrors}
        handleCallback={handleCallback}
        setValue={setValue}
      />
    );
  }

  return (
    <div className={className}>
      {renderButton(handleAddDefault, appendValue, t)}
    </div>
  );
}

export default memo(QuestionFieldOptionsAddItemComponent);
